import { useSWR } from '@tiger/swr';
import { Button, Dropdown, Form, Input } from 'antd';
import React, { Fragment, useMemo, useState } from 'react';
import { $Formutil, withForm } from 'react-antd-formutil';
import { CloseOutlined } from '@ant-design/icons';
import FormItem from 'components/FormItem';
import { useConfirmModal } from 'components/Modal';
import API, { APICollection, getAPIEnv, removeAPIEnv, saveAPIEnv } from 'utils/API';

export interface APIManagerProps {
    onClose(): void;
    $formutil: $Formutil;
}

const apiIndex = 0;

const APIManager: React.FC<APIManagerProps> = ({ onClose }) => {
    const [currentEnv] = useState(() => getAPIEnv() || 'test');
    const {
        data: envOptions,
        isLoading,
        mutate,
        error
    } = useSWR(
        {
            url: API.CG.environments(),
            params: {
                appName: 'TradeUP'
            }
        },
        {
            immutable: true,
            dataChain: resp => resp.environments
        }
    );
    const { open: openConfirmModal } = useConfirmModal();

    const apiFormItems = useMemo(() => {
        return Object.keys(APICollection).map(name => {
            const { HOST, HOST_ALIAS, REGION_HOSTS } = APICollection[name];

            return (
                <Fragment key={name}>
                    <FormItem label={name} name={name} $defaultValue={HOST[apiIndex]}>
                        <Input size="small" readOnly />
                    </FormItem>
                    {REGION_HOSTS && (
                        <div className="ml-3">
                            {Object.keys(REGION_HOSTS).map(alias => {
                                return (
                                    <FormItem
                                        name={`${name}:${alias}`}
                                        key={`${name}:${alias}`}
                                        label={`${name}:${alias}`}
                                        $defaultValue={REGION_HOSTS[alias][apiIndex]}>
                                        <Input size="small" readOnly />
                                    </FormItem>
                                );
                            })}
                        </div>
                    )}
                    {HOST_ALIAS && (
                        <div className="ml-3">
                            {Object.keys(HOST_ALIAS).map(alias => {
                                return (
                                    <FormItem
                                        name={`${name}:${alias}`}
                                        key={`${name}:${alias}`}
                                        label={`${name}:${alias}`}
                                        $defaultValue={HOST_ALIAS[alias][apiIndex]}>
                                        <Input size="small" readOnly />
                                    </FormItem>
                                );
                            })}
                        </div>
                    )}
                </Fragment>
            );
        });
    }, []);

    const onSubmit = (env: string) => {
        saveAPIEnv(env);

        openConfirmModal({
            content: `将切换到${env}环境域名，该配置在页面刷新后生效，立即刷新？`,
            onOk: () => {
                onClose();

                window.location.reload();
            }
        });
    };

    return (
        <div className="api-manager-root">
            <div className="manager-head">
                <div className="d-flex justify-content-between align-items-center">
                    <div className="d-flex align-items-center">
                        <Button
                            className="d-flex align-items-center"
                            size="small"
                            type="text"
                            onClick={() => onClose()}>
                            <CloseOutlined />
                        </Button>
                        <h3 className="mb-0">API环境配置</h3>
                    </div>
                    <Dropdown
                        menu={{
                            items:
                                envOptions?.map(env => ({
                                    label: <div onClick={() => onSubmit(env)}>切换至{env}</div>,
                                    key: env
                                })) || [],
                            selectedKeys: [currentEnv]
                        }}
                        overlayClassName="api-env-menu">
                        <Button type="primary" loading={isLoading} onClick={error ? () => mutate() : undefined}>
                            {error ? '环境获取失败，请刷新' : '切换环境'}
                        </Button>
                    </Dropdown>
                </div>
                <hr />
            </div>
            <Form layout="vertical" noValidate className="manager-body">
                <h4>当前环境：{currentEnv || '默认环境'}</h4>
                {apiFormItems}
                <div className="manager-foot">
                    <hr />
                    <Button type="primary" className="mt-2" htmlType="submit" block>
                        保存
                    </Button>
                    <Button
                        className="mt-2 text-body"
                        htmlType="button"
                        type="link"
                        block
                        onClick={() => {
                            openConfirmModal({
                                content: '页面将会刷新，并重置接口环境为默认配置？',
                                onOk: () => {
                                    removeAPIEnv();

                                    window.location.reload();
                                }
                            });
                        }}>
                        恢复默认
                    </Button>
                </div>
            </Form>
        </div>
    );
};

export default withForm(APIManager);
